import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ScanSearch, Loader2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { cardConsoleApi } from '../../services/iso8583Api';
import { PageHeader, Card, CodeBlock, Table, useSignOnSample } from './common';

const PLACEHOLDER = 'paste a frame here — hex, a hex dump, with or without the 2-byte length prefix';

/**
 * Read a frame you already have — from your own gateway's log, or from a trace
 * of the switch you are really joining — and see what it says. Nothing is sent,
 * so a response, a half-built message or a frame belonging to somebody else is
 * all safe to paste.
 */
export default function CardExplainFrame() {
  const [input, setInput] = useState('');
  // The example is a sign-on the engine built in the dialect in force, so it always reads.
  const { sample } = useSignOnSample();
  const example = sample?.dump || sample?.frameHex || '';
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const explain = async () => {
    setBusy(true);
    try {
      setResult(await cardConsoleApi.explain(input));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader icon={ScanSearch} title="Explain a Frame">
        Paste an ISO 8583 frame and read it back: the message type, every data element with this dialect's name and
        format, both bitmaps, and anything the codec finds wrong — with the byte it was found at.
      </PageHeader>

      <Card title="The frame" icon={ScanSearch}>
        <p className="text-sm text-gray-700 mb-2">
          Hex, with or without the 2-byte length prefix. Spaces, newlines and a whole hex dump all paste — it is
          usually copied out of a log. <strong>Nothing is sent, stored or scored</strong>, and no link is opened:
          this only reads. Use test data, as everywhere on this corridor.
        </p>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={5}
          placeholder={example || PLACEHOLDER}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 font-mono text-xs text-gray-900" />
        <div className="flex items-center gap-2 mt-3">
          <button type="button" onClick={explain} disabled={busy || !input.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" />}
            Explain it
          </button>
          <button type="button" onClick={() => { setInput(example); setResult(null); }} disabled={!example}
            className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">
            Use the example
          </button>
        </div>
      </Card>

      {result?.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">{result.error}</div>
      )}

      {result && !result.error && <Explanation result={result} />}
    </div>
  );
}

function Explanation({ result }) {
  const { frame, prefix, mti, bitmaps, fields, findings, decoded, notes } = result;
  return (
    <>
      <Card title="What it is">
        <div className="flex items-start gap-2 mb-3">
          {decoded ? <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
            : <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />}
          <div>
            <p className="text-sm text-gray-900">
              <span className="font-mono font-semibold">{mti.value || '????'}</span> — {mti.name}
              {mti.answeredBy && <> · answered with <span className="font-mono">{mti.answeredBy}</span></>}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {[mti.request && 'a request', mti.response && 'a response', mti.advice && 'an advice',
                mti.repeat && 'marked as a repeat'].filter(Boolean).join(' · ') || 'not a message type this dialect names'}
              {' · '}{frame.bytes} bytes as pasted · dialect <span className="font-mono">{result.dialect}</span>
            </p>
          </div>
        </div>

        <Table head={['Length prefix', '']} rows={[[
          prefix.present
            ? <span className="font-mono">{prefix.declared} declared · {prefix.actual} followed</span>
            : <span className="text-gray-500">none in what you pasted</span>,
          <span className={prefix.agrees ? 'text-gray-700' : 'text-red-700'}>{prefix.note}</span>,
        ]]} />

        {notes?.length > 0 && (
          <div className="mt-3 space-y-1">
            {notes.map((n) => <p key={n} className="text-sm text-amber-900">{n}</p>)}
          </div>
        )}
      </Card>

      {findings?.length > 0 && (
        <Card title={`Findings (${findings.length})`} icon={AlertTriangle}>
          <Table head={['Severity', 'Code', 'Where', 'Byte', 'Expected', 'Found', 'What it means']} mono={[1, 3]}
            rows={findings.map((f) => [
              <span className={f.severity === 'ERROR' ? 'text-red-700 font-medium' : 'text-amber-800'}>{f.severity}</span>,
              f.code,
              f.path ? `DE ${f.path}` : (f.de ? `DE ${f.de}` : 'message'),
              f.byteOffset,
              f.expected || '—',
              f.actual || '—',
              f.explanation,
            ])} />
        </Card>
      )}

      <Card title="Data elements">
        <Table head={['DE', 'Name', 'Format', 'Value', 'What it means']} mono={[0, 2, 3]}
          rows={fields.map((f) => [f.de, f.name, f.format, f.value, f.meaning || ''])} />
        {fields.length === 0 && <p className="text-sm text-gray-500">No data element decoded.</p>}
      </Card>

      <Card title="Bitmaps">
        <div className="space-y-2">
          {bitmaps.map((b) => (
            <div key={b.half} className="rounded-lg border border-gray-200 p-2.5">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {b.half === 'PRIMARY' ? 'Primary · DE 1–64' : 'Secondary · DE 65–128'}
                </span>
                <span className="font-mono text-xs text-gray-900">{b.hex}</span>
              </div>
              <p className="font-mono text-[11px] text-gray-600 mt-1 break-all">{b.bits}</p>
              <p className="text-xs text-gray-700 mt-1">
                Marks present:{' '}
                {b.fields?.length
                  ? b.fields.map((de) => <span key={de} className="font-mono">DE {de} </span>)
                  : <span className="text-gray-500">nothing</span>}
              </p>
              <p className="text-[11px] text-gray-500 mt-0.5">{b.note}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="The bytes">
        <CodeBlock>{frame.dump}</CodeBlock>
        <p className="text-xs text-gray-500 mt-3 mb-1">As ASCII:</p>
        <CodeBlock>{frame.ascii}</CodeBlock>
        <p className="text-xs text-gray-500 mt-3 mb-1">As a shell string, if you want to send it:</p>
        <CodeBlock>{frame.printf}</CodeBlock>
        <p className="text-xs text-gray-500 mt-2">
          To put a frame on a link and see the switch answer, use the{' '}
          <Link to="/iso8583/message-generator" className="text-primary-600 hover:underline">Message Generator</Link>.
        </p>
      </Card>
    </>
  );
}
